import type { Settings, Vehicle } from '../types';
import { DEMO_SETTINGS_KEY, DEMO_VEHICLES_KEY } from './demoApi';

const VEHICLES_KEY = 'precisionEV.vehicles.v2';
const SETTINGS_KEY = 'precisionEV.settings.v2';

function isDemoLocation() {
  return typeof location !== 'undefined' && (/^\/(developer|workshop)\/?$/.test(location.pathname) || new URL(location.href).searchParams.get('demo') === '1');
}

function isSupportPreviewLocation() {
  return typeof location !== 'undefined' && new URLSearchParams(location.hash.slice(1)).get('support-preview') === '1';
}

function vehiclesKey() { return isSupportPreviewLocation() ? 'precisionEV.supportPreview.vehicles' : isDemoLocation() ? DEMO_VEHICLES_KEY : VEHICLES_KEY; }
function settingsKey() { return isSupportPreviewLocation() ? 'precisionEV.supportPreview.settings' : isDemoLocation() ? DEMO_SETTINGS_KEY : SETTINGS_KEY; }

export const defaultSettings: Settings = { theme: 'light', language: 'pt', currency: 'EUR', distance: 'km', consumption: 'kwh100km', fuel:'l100km' };

function safeParse<T>(value: string | null, fallback: T): T {
  try { return value ? JSON.parse(value) as T : fallback; } catch { return fallback; }
}

function safeGet(key: string) { try { return localStorage.getItem(key); } catch { return null; } }
function safeSet(key: string, value: string) { try { localStorage.setItem(key, value); } catch { /* Keep the current session usable when storage is blocked. */ } }
function safeRemove(key: string) { try { localStorage.removeItem(key); } catch { /* Nothing persisted, nothing to remove. */ } }

function migrateLegacy(): Vehicle[] {
  const old = safeParse<any[]>(safeGet('precisionGarage'), []);
  return old.map((car, index) => ({
    id: car.id || crypto.randomUUID(),
    name: car.name || 'Veículo elétrico',
    variant: car.version || car.variant || '',
    year: Number(car.year) || undefined,
    vin: car.vin || car.tvpfVin || '',
    mileage: Number(car.km) || undefined,
    photo: car.photo || '',
    soh: Number(car.soh) || undefined,
    delta: Number(car.delta) || undefined,
    primary: index === 0,
    precisionCertified: Boolean(car.tvpf),
    tvpfToken: car.tvpfToken,
    tvpfProgress: Number(car.progress) || undefined,
    lastUpdated: car.updatedAt || undefined,
    events: []
  }));
}

export function loadVehicles(): Vehicle[] {
  const current = safeParse<Vehicle[]>(safeGet(vehiclesKey()), []);
  if (current.length) return current.map(v=>({...v,events:v.events||[],batteryMeasurements:v.batteryMeasurements||[]}));
  if (isDemoLocation() || isSupportPreviewLocation()) return [];
  const migrated = migrateLegacy();
  if (migrated.length) saveVehicles(migrated);
  return migrated;
}

export function saveVehicles(vehicles: Vehicle[]) { safeSet(vehiclesKey(), JSON.stringify(vehicles)); }
export function clearLocalVehicles() {
  safeRemove(vehiclesKey());
  if (!isDemoLocation() && !isSupportPreviewLocation()) safeRemove('precisionGarage');
}
export function loadSettings(): Settings { return {...defaultSettings, ...safeParse<Partial<Settings>>(safeGet(settingsKey()), {})}; }
export function saveSettings(settings: Settings) { safeSet(settingsKey(), JSON.stringify(settings)); }
